/**
 * Preferences Cache
 *
 * Manages the profile cache (server data) and reads activeCourseId from device preferences.
 *
 * Write: save raw server data to profile cache
 * Read: merge profile cache + device activeCourseId into CachedPreferences for the store
 */

import {
  UserProfileCacheStorage,
  PreferencesStorage,
  pushPreferences,
  type UserProfileCacheData,
} from '../generated/sync/sync-config';
import { createLogger } from '../utils/logger';

const log = createLogger('PreferencesCache');

/** Per-course preference shape for cache serialization (mirrors CoursePreference from store) */
export interface CachedCoursePreference {
  courseId: string;
  country: string;
  sourceLocale: string;
  targetLocale: string;
  lessonLanguage: string;
  azureTtsVoice: string;
  ttsProvider: string;
  sttProvider: string;
}

export interface CachedPreferences {
  displayName: string;
  debugContextSnapshots: boolean;
  lessonFeedbackEnabled: boolean;
  voiceExercisesEnabled: boolean;
  activeCourseId: string | null;
  coursePreferences: CachedCoursePreference[];
}

/** Syncable preference field names */
export type SyncablePreferenceKey = keyof CachedPreferences;

const DEFAULTS: CachedPreferences = {
  displayName: '',
  debugContextSnapshots: false,
  lessonFeedbackEnabled: false,
  voiceExercisesEnabled: true,
  activeCourseId: null,
  coursePreferences: [],
};

/**
 * Load cached preferences by merging server profile cache + device activeCourseId.
 * Returns null if no profile cache exists.
 */
export async function loadCachedPreferences(): Promise<CachedPreferences | null> {
  try {
    const profile = await UserProfileCacheStorage.load();
    if (!profile) return null;

    const devicePrefs = await PreferencesStorage.load();

    return {
      ...DEFAULTS,
      ...profile,
      activeCourseId: devicePrefs?.activeCourseId ?? DEFAULTS.activeCourseId,
    } as CachedPreferences;
  } catch (error) {
    log.warn('Failed to load cached preferences', { error });
    return null;
  }
}

/**
 * Save profile data to cache and optionally update activeCourseId in device preferences.
 * The full object is persisted as-is — no field filtering.
 */
export async function saveProfileCache(
  data: UserProfileCacheData,
  localState?: { activeCourseId?: string | null },
): Promise<void> {
  try {
    await UserProfileCacheStorage.save(data);
    if (localState?.activeCourseId !== undefined) {
      await PreferencesStorage.update({ activeCourseId: localState.activeCourseId });
    }
  } catch (error) {
    log.warn('Failed to save profile cache', { error });
  }
}

/**
 * Update one or more preference fields in the cache.
 * Server fields go to profile cache, activeCourseId goes to device preferences.
 * Syncable fields are automatically enqueued to the sync queue.
 */
export async function updateCachedPreferences(
  updates: Partial<Record<SyncablePreferenceKey, unknown>>,
): Promise<void> {
  try {
    const { activeCourseId, ...serverUpdates } = updates as Partial<CachedPreferences>;

    // Update server fields in profile cache
    if (Object.keys(serverUpdates).length > 0) {
      await UserProfileCacheStorage.update(serverUpdates);
    }

    // Update activeCourseId in device preferences
    if (activeCourseId !== undefined) {
      await PreferencesStorage.update({ activeCourseId });
    }

    // Auto-enqueue syncable fields via generated push function
    if (Object.keys(serverUpdates).length > 0) {
      pushPreferences(serverUpdates as Parameters<typeof pushPreferences>[0]);
    }
  } catch (error) {
    log.warn('Failed to update cached preferences', { error });
  }
}

/**
 * Clear cached preferences (e.g., on logout).
 */
export async function clearCachedPreferences(): Promise<void> {
  try {
    await UserProfileCacheStorage.clear();
    // activeCourseId survives logout (device-local preference)
  } catch (error) {
    log.warn('Failed to clear cached preferences', { error });
  }
}
